<script setup lang="ts">
import { computed, ref } from 'vue'
import type { BookPageConfig } from '../configs/types'
import { t } from '@/i18n/utils'
import { formatCompactNumber } from '@/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
  currentContentId?: string | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
  (event: 'audio'): void
}>()

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

const hasAudio = computed(() => props.book?.hasAudio === 1)

const summaryExpanded = ref(false)
const introduceText = computed(() => props.book?.introduce || '')

function onAudioClick() {
  emit('audio')
}
</script>

<template>
  <div class="novelyn-book-page">
    <!-- 封面区域：柔和玫瑰光晕 -->
    <div
      class="novelyn-book-hero relative"
      :class="{ 'mt-60px': bannerShow }"
    >
      <!-- 玫瑰光晕背景 -->
      <div class="novelyn-hero-glow" />

      <!-- 返回按钮 -->
      <div
        class="novelyn-book-back absolute top-12px left-12px z-99"
        @click="emit('back')"
      >
        <i-icon-park-outline-left class="w-4 h-4" />
      </div>

      <!-- 封面内容 -->
      <div class="novelyn-book-main z-1 relative">
        <img
          class="novelyn-book-cover"
          :src="coverUrl"
          alt="Book Cover"
        >
        <div class="novelyn-book-meta">
          <h1 class="novelyn-book-title">
            {{ book.contentName }}
          </h1>
          <p class="novelyn-book-byline">
            <span class="novelyn-status">{{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
          </p>
          <p class="novelyn-book-author">
            {{ book.authorInfo?.author }}
            <template v-if="book.viewUsers">
              <span class="novelyn-dot-sep" />
              <i-assets-icon-view-count class="w-13px h-10px flex-shrink-0" />
              <span>{{ formatCompactNumber(book.viewUsers) }} {{ t('Reads') }}</span>
            </template>
          </p>
          <div class="novelyn-hero-tags">
            <span
              v-for="tag in book.tagList || []"
              :key="tag.id"
              @click="emit('goto-tags', tag.id || '')"
            >{{ tag.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary 简介卡片 -->
    <div class="novelyn-summary-card bg-n-bg-card">
      <div class="novelyn-quote-mark">
        "
      </div>
      <p
        class="novelyn-summary-text"
        :class="{ 'is-expanded': summaryExpanded }"
      >
        {{ introduceText }}
      </p>
      <div
        v-if="introduceText.length > 60"
        class="novelyn-summary-toggle"
        @click="summaryExpanded = !summaryExpanded"
      >
        {{ summaryExpanded ? t('ShowLess') : t('Expand') }} {{ summaryExpanded ? '▴' : '▾' }}
      </div>
    </div>

    <!-- Catalogue 目录 -->
    <div class="novelyn-toc-head">
      <p class="novelyn-section-title">
        {{ config.texts.catalogue }}
      </p>
      <div
        class="novelyn-more-link text-13px flex items-center justify-end cursor-pointer"
        @click="emit('open-toc')"
      >
        <p class="whitespace-nowrap">
          {{ book.lastChapter?.sequenceNo || 0 }} {{ config.texts.chapters }}
        </p>
        <i-icon-park-outline-right class="w-4 h-4" />
      </div>
    </div>

    <!-- 第一章试读 -->
    <div class="novelyn-sample">
      <!-- 引导语 -->
      <div v-if="introduce" class="novelyn-sample-text mt-20px">
        <p v-html="introduce" />
      </div>

      <p class="novelyn-chapter-title">
        {{ book.firstChapter?.chapterName }}
      </p>

      <div class="novelyn-sample-text">
        <p v-for="(item, index) in paragraphs.slice(0, 16)" :key="index" class="mt-4">
          {{ item }}
        </p>
      </div>

      <!-- 分割线 -->
      <div class="novelyn-divider-fancy">
        {{ config.texts.readFirstChapter }}
      </div>
    </div>

    <!-- TOC 弹窗 -->
    <BizPopupBookTOC
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :lock-icon-color="config.theme.tocLockColor"
    />

    <!-- 底部固定按钮 -->
    <BasicFixedBottom class="novelyn-book-bottom">
      <div class="novelyn-bottom-bar bg-n-bg-card">
        <div
          class="novelyn-bottom-icon"
          :class="{ 'opacity-50': shelfLoading }"
          @click="emit('toggle-shelf')"
        >
          <svg
            class="novelyn-shelf-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 20s-7-4.3-9.3-8.6C.8 7.9 3 4.5 6.4 4.5c2 0 3.6 1 4.6 2.6 1-1.6 2.6-2.6 4.6-2.6 3.4 0 5.6 3.4 3.7 6.9C17 15.7 12 20 12 20z" />
            <path d="M12 8v4M10 10h4" stroke-width="1.8" />
          </svg>
          <span>{{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}</span>
        </div>
        <a
          class="novelyn-bottom-read"
          @click="emit('read')"
        >
          {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
        </a>
        <!-- 音频入口 -->
        <div
          v-if="hasAudio"
          class="novelyn-bottom-icon is-lav"
          @click="onAudioClick()"
        >
          <i-icon-park-outline-headset class="w-18px h-18px" />
          <span>{{ t('Audio') }}</span>
        </div>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style lang="scss" scoped>
.novelyn-book-page {
  color: var(--novelyn-ink);
  background: var(--novelyn-bg);
}

.novelyn-book-hero {
  position: relative;
  min-height: 224px;
  padding: 56px 20px 24px;
  overflow: hidden;
}

.novelyn-hero-glow {
  position: absolute;
  inset: -30px;
  background: linear-gradient(160deg, #F7A8C0, #D94F78);
  filter: blur(38px);
  opacity: .45;
}

.novelyn-book-main {
  display: flex;
  align-items: center;
  gap: 18px;
}

.novelyn-book-cover {
  width: 108px;
  height: 144px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 14px 30px rgba(201, 63, 104, .35);
}

.novelyn-book-meta {
  min-width: 0;
  flex: 1;

  h1 {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 21px;
    font-weight: 800;
    line-height: 1.25;
  }
}

.novelyn-book-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
}

.novelyn-book-byline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
}

.novelyn-book-author {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  color: var(--novelyn-ink-2);
  font-size: 12px;
  font-weight: 700;
}

.novelyn-dot-sep::before {
  content: '·';
  margin: 0 5px;
}

.novelyn-status,
.novelyn-hero-tags span {
  display: inline-block;
  padding: 3px 9px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.4;
  color: var(--novelyn-rose-deep);
  border: 1px solid var(--novelyn-line);
  border-radius: 999px;
  background: var(--novelyn-rose-faint);
}

.novelyn-book-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: var(--novelyn-ink);
  background: rgba(255, 255, 255, .9);
  border-radius: 50%;
  box-shadow: var(--novelyn-shadow-sm);
}

.novelyn-summary-card {
  position: relative;
  z-index: 2;
  margin: -6px 16px 0;
  padding: 16px;
  border-radius: 20px;
  box-shadow: var(--novelyn-shadow-sm);
}

.novelyn-quote-mark {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 34px;
  line-height: .6;
  color: var(--novelyn-rose);
}

.novelyn-summary-text {
  display: -webkit-box;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.7;
  color: #6B5560;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;

  &.is-expanded {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
  }
}

.novelyn-summary-toggle {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 900;
  color: var(--novelyn-rose);
  text-align: center;
  cursor: pointer;
}

.novelyn-toc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  margin: 22px 0 10px;
}

.novelyn-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 18px;
  font-weight: 700;
  white-space: nowrap;

  &::before {
    content: '';
    width: 4px;
    height: 16px;
    border-radius: 4px;
    background: var(--novelyn-gradient);
  }
}

.novelyn-more-link {
  color: var(--novelyn-rose);
  font-weight: 800;
}

.novelyn-hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  font-size: 10px;
  font-weight: 800;
}

.novelyn-sample {
  padding: 0 20px;
  margin-top: 20px;
}

.novelyn-sample-text {
  font-size: 13.5px;
  line-height: 1.9;
  color: #57424C;
}

.novelyn-chapter-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .2px;
  margin-bottom: 10px;
}

.novelyn-divider-fancy {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--novelyn-ink-3);
  font-size: 11px;
  font-weight: 700;
  margin: 16px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--novelyn-line);
  }
}

.novelyn-bottom-bar {
  display: flex;
  align-items: center;
  width: 100%;
}

.novelyn-bottom-icon {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 58px;
  height: 46px;
  border-radius: 16px;
  background: var(--novelyn-rose-faint);
  border: 1px solid var(--novelyn-line);
  color: var(--novelyn-rose-deep);
  font-size: 8px;
  font-weight: 900;
  cursor: pointer;

  &.is-lav {
    background: var(--novelyn-lavender-soft);
    color: var(--novelyn-lavender-deep);
  }
}

.novelyn-shelf-icon {
  display: block;
  flex-shrink: 0;
}

.novelyn-bottom-read {
  flex: 1;
  height: 46px;
  line-height: 46px;
  padding: 0 !important;
  text-align: center;
  font-size: 14px;
  font-weight: 800;
  color: #fff !important;
  border-radius: 999px !important;
  background: var(--novelyn-gradient);
  box-shadow: 0 8px 18px rgba(224, 90, 126, .35);
  cursor: pointer;
}
</style>
